import React,{ useContext, useState, useRef, createContext, useEffect } from "react";

interface GeneratedImage {
    url: string;
    width: number;
    height: number;
    content_type: string;
  }
  
  interface ImageStatusResponse {
    status: string;
    payload?: {
      images?: GeneratedImage[];
    };
  }
  
  interface ImageItem {
    _id: string;
    userId: string;
    modelId: string;
    status: string;
    statusResponse?: ImageStatusResponse;
    inputData?: {
      prompt?: string;
      imageUrl?: string;
    };
  }
  
interface ImageWebSocketContextProps {
  imageItem: ImageItem | null;
  imageUrll: string[];
  socketPrompt: string | null;
  sendMessage: (message: any) => void;
  reconnectWebSocket: () => void; // ✅ added
}

const ImageWebSocketContext = createContext<ImageWebSocketContextProps | undefined>(undefined);

export const ImageSocketProvider: React.FC<{ userId: string; modelId: string; children: React.ReactNode }> = ({
  userId,
  modelId,
  children,
}) => {
  const socketRef = useRef<WebSocket | null>(null);
  const reconnectTimer = useRef<NodeJS.Timeout | null>(null); 
  const [imageItem, setImageItem] = useState<ImageItem | null>(null);
  const [imageUrll, setImageUrls] = useState<string[]>([]);
  const [socketPrompt, setSocketPrompt] = useState<string | null>(null);

  const socketURL = `${process.env.REACT_APP_WEB_SOCKET_URL}image?userId=${userId}&modelId=${modelId}`;

  const connectWebSocket = React.useCallback(() => {
    const socket = new WebSocket(socketURL);

    socket.onopen = () => {
      console.log("Image WebSocket connected:", socket.url);
    };

    socket.onmessage = (event) => {
      console.log("WebSocket message received:", event.data);
      try {
        const parsed = JSON.parse(event.data);
        const item = parsed?.data?.[0];
        const urls = item?.statusResponse?.payload?.images?.map((img: any) => img.url) || [];

        setImageItem(item);
        setImageUrls(urls);
        setSocketPrompt(item?.inputData?.prompt ?? null);
      } catch (err: any) {
        console.error("Failed to parse Image WebSocket message:", err);
      }
    };

    socket.onerror = (err) => {
      console.error("Image WebSocket error:", err);
    };

    socketRef.current = socket;
  }, [socketURL]);

  const closeWebSocket = () => {
       console.log("Closing WebSocket...1");
    if (socketRef.current) {
      socketRef.current.close();
      socketRef.current = null;
    }
    if (reconnectTimer.current) {
      clearTimeout(reconnectTimer.current);
      reconnectTimer.current = null;
    }
  };

 const reconnectWebSocket = () => {
    closeWebSocket();
      console.log("Reconnecting WebSocket...");
    connectWebSocket();
  };

 useEffect(() => {
    console.log("ids", userId, modelId);
    if (!userId || !modelId) return;
    connectWebSocket();

  }, [userId, modelId, connectWebSocket]);

  const sendMessage = (message: any) => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify(message));
    } else {
      console.warn("Image WebSocket not open. Message not sent:", message);
    }
  };

  return (
    <ImageWebSocketContext.Provider
      value={{ sendMessage, imageItem, imageUrll, socketPrompt, reconnectWebSocket }}
    >
      {children}
    </ImageWebSocketContext.Provider>
  );
};

export const useImageWebSocket = () => {
  const context = useContext(ImageWebSocketContext);
  if (!context) {
    throw new Error("useImageWebSocket must be used within an ImageSocketProvider");
  }
  return context;
};